import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, errMsg } from "../../lib/api";
import { Card, Btn, Input, EmptyState } from "../../components/UI";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Upload, Download, TrendingUp, TrendingDown, Minus,
  Sparkles, Scale, BarChart3, Newspaper, Camera, Link2, Tv, Target, MessageSquare, Rocket, ArrowUpRight,
} from "lucide-react";

const LINE_COLORS = ["#22d3ee", "#f87171", "#fbbf24", "#a78bfa", "#34d399", "#f472b6"];

const namesOf = (data) => data.polling.averages.map(a => a.name);

const MomentumIcon = ({ direction }) =>
  direction === "rising" ? <TrendingUp className="w-4 h-4 text-emerald-300" />
    : direction === "falling" ? <TrendingDown className="w-4 h-4 text-rose-300" />
    : <Minus className="w-4 h-4 text-slate-400" />;

// ---------- Overview ----------
export const OverviewTab = ({ data, goTo }) => {
  const { polling, issues } = data;
  const mine = polling.averages.find(a => a.is_mine);
  const hasPolls = polling.poll_count > 0;

  return (
    <div className="space-y-4" data-testid="overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-poll-avg">
          <div className="text-xs uppercase tracking-wider text-slate-400">Polling average</div>
          <div className="mt-2 font-display text-3xl font-bold text-cyan-300">{mine?.avg != null ? `${mine.avg}%` : "—"}</div>
          <div className="text-[10px] text-slate-500 mt-1">{polling.method_note}</div>
        </Card>
        <Card data-testid="stat-margin">
          <div className="text-xs uppercase tracking-wider text-slate-400">Margin vs top opponent</div>
          <div className={`mt-2 font-display text-3xl font-bold ${polling.margin > 0 ? "text-emerald-300" : polling.margin < 0 ? "text-rose-300" : "text-white"}`}>
            {polling.margin != null ? `${polling.margin > 0 ? "+" : ""}${polling.margin}` : "—"}
          </div>
        </Card>
        <Card data-testid="stat-polls-logged">
          <div className="text-xs uppercase tracking-wider text-slate-400">Polls logged</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">{polling.poll_count}</div>
          <div className="text-[10px] text-slate-500 mt-1">{polling.last_poll_date ? `Latest ${polling.last_poll_date}` : "Manual + CSV upload"}</div>
        </Card>
        <Card data-testid="stat-issues-tracked">
          <div className="text-xs uppercase tracking-wider text-slate-400">Issues tracked</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">{issues.length}</div>
        </Card>
      </div>

      {!hasPolls ? (
        <Card>
          <EmptyState icon={BarChart3} title="No polling data yet"
            description="Add polls manually or upload a CSV in the Polls tab — TotalReach computes weighted averages, trendlines and momentum from your data."
            action={<Btn onClick={() => goTo("polls")} data-testid="overview-goto-polls"><Plus className="w-4 h-4" /> Add polling data</Btn>} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2" data-testid="trendline-chart">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold">Polling trendline</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 uppercase">Your data</span>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <LineChart data={polling.trendline}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => (v || "").slice(5)} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit="%" domain={["dataMin - 5", "dataMax + 5"]} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {namesOf(data).map((n, i) => (
                    <Line key={n} type="monotone" dataKey={n} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={i === 0 ? 2.5 : 1.5} dot={{ r: 2 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card data-testid="momentum-card">
            <h3 className="font-display text-lg font-bold mb-3">Momentum</h3>
            {polling.momentum.length === 0 && <div className="text-sm text-slate-500">Add at least 2 polls to compute momentum.</div>}
            <div className="space-y-3">
              {polling.momentum.map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <MomentumIcon direction={m.direction} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{m.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{m.direction}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold">{m.current}%</div>
                    <div className={`text-xs ${m.delta > 0 ? "text-emerald-300" : m.delta < 0 ? "text-rose-300" : "text-slate-500"}`}>
                      {m.delta > 0 ? "+" : ""}{m.delta}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {issues.length > 0 && (
        <Card data-testid="overview-issues">
          <h3 className="font-display text-lg font-bold mb-3">Top public issues</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {issues.slice(0, 6).map((i) => (
              <div key={i.issue} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-sm text-white truncate">{i.issue}</div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-cyan-400/70" style={{ width: `${i.latest_salience}%` }} /></div>
                <div className="mt-1.5 flex justify-between text-xs">
                  <span className="text-slate-500">Salience {i.latest_salience}</span>
                  <span className={i.latest_net_sentiment >= 0 ? "text-emerald-300" : "text-rose-300"}>Net {i.latest_net_sentiment > 0 ? "+" : ""}{i.latest_net_sentiment}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ---------- Polls ----------
export const PollsTab = ({ id, data, reload }) => {
  const names = namesOf(data);
  const [polls, setPolls] = useState(null);
  const [form, setForm] = useState({ pollster: "", date: "", sample_size: "", moe: "" });
  const [pcts, setPcts] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadPolls = useCallback(() => {
    api.get(`/political-intelligence/candidates/${id}/polls`).then(r => setPolls(r.data));
  }, [id]);
  useEffect(() => { loadPolls(); }, [loadPolls]);

  const addPoll = async () => {
    const results = names.filter(n => pcts[n] !== undefined && pcts[n] !== "").map(n => ({ name: n, pct: parseFloat(pcts[n]) }));
    if (!form.pollster.trim() || !form.date) { toast.error("Pollster and date are required"); return; }
    if (results.length === 0) { toast.error("Enter at least one candidate percentage"); return; }
    setSaving(true);
    try {
      await api.post(`/political-intelligence/candidates/${id}/polls`, {
        pollster: form.pollster, date: form.date,
        sample_size: form.sample_size ? parseInt(form.sample_size) : null,
        moe: form.moe ? parseFloat(form.moe) : null, results,
      });
      toast.success("Poll added");
      setForm({ pollster: "", date: "", sample_size: "", moe: "" });
      setPcts({});
      loadPolls(); reload();
    } catch (e) { toast.error(errMsg(e, "Could not add poll")); }
    finally { setSaving(false); }
  };

  const downloadTemplate = () => {
    const q = (s) => (s.includes(",") ? `"${s}"` : s);
    const rows = ["date,pollster,sample_size,moe,candidate_name,pct"];
    names.forEach((n, i) => rows.push(`2026-05-15,Example Research,800,3.5,${q(n)},${(45 - i * 3).toFixed(1)}`));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "totalreach_polls_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const r = await api.post(`/political-intelligence/candidates/${id}/polls/csv`, { csv_text: text });
      const { imported_polls, errors, unknown_names } = r.data;
      toast.success(`Imported ${imported_polls} poll${imported_polls === 1 ? "" : "s"}`);
      if (unknown_names?.length) toast.warning(`Skipped unknown names: ${unknown_names.join(", ")}`);
      if (errors?.length) toast.warning(errors[0]);
      loadPolls(); reload();
    } catch (err) { toast.error(errMsg(err, "CSV import failed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePoll = async (pid) => {
    try {
      await api.delete(`/political-intelligence/candidates/${id}/polls/${pid}`);
      toast.success("Poll deleted");
      loadPolls(); reload();
    } catch (e) { toast.error(errMsg(e, "Could not delete poll")); }
  };

  return (
    <div className="space-y-4" data-testid="polls-tab">
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="add-poll-card">
          <h3 className="font-display text-lg font-bold mb-3">Add a poll</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input label="Pollster" value={form.pollster} onChange={(e) => setForm({ ...form, pollster: e.target.value })}
              placeholder="Marist, Emerson…" data-testid="poll-pollster-input" />
            <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="poll-date-input" />
            <Input label="Sample size" type="number" value={form.sample_size} onChange={(e) => setForm({ ...form, sample_size: e.target.value })}
              placeholder="800" data-testid="poll-sample-input" />
            <Input label="MoE ±" type="number" step="0.1" value={form.moe} onChange={(e) => setForm({ ...form, moe: e.target.value })}
              placeholder="3.5" data-testid="poll-moe-input" />
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {names.map((n, i) => (
              <div key={n}>
                <label className="text-xs text-slate-400 block mb-1.5 truncate" title={n}>
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: LINE_COLORS[i % LINE_COLORS.length] }} />{n} %
                </label>
                <input type="number" step="0.1" min="0" max="100" value={pcts[n] ?? ""} data-testid={`poll-pct-${i}`}
                  onChange={(e) => setPcts({ ...pcts, [n]: e.target.value })} placeholder="45.0"
                  className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Btn onClick={addPoll} disabled={saving} data-testid="add-poll-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add poll
            </Btn>
          </div>
        </Card>

        <Card data-testid="csv-upload-card">
          <h3 className="font-display text-lg font-bold mb-2">CSV upload</h3>
          <p className="text-xs text-slate-400 leading-relaxed">One row per candidate per poll. Columns: <code className="text-cyan-300">date, pollster, sample_size, moe, candidate_name, pct</code>. Names must match your tracked candidates exactly.</p>
          <div className="mt-4 space-y-2">
            <Btn variant="secondary" className="w-full justify-center" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4" /> Download template
            </Btn>
            <Btn className="w-full justify-center" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
            </Btn>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
          </div>
        </Card>
      </div>

      <Card padded={false} data-testid="polls-table-card">
        <div className="p-5 pb-0 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Poll log</h3>
          <span className="text-xs text-slate-500">{polls?.length ?? 0} polls</span>
        </div>
        {!polls ? <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          : polls.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No polls yet — add one above or upload a CSV.</div>
          : (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                    <th className="px-5 py-2.5">Date</th><th className="px-3 py-2.5">Pollster</th>
                    <th className="px-3 py-2.5">n</th><th className="px-3 py-2.5">MoE</th>
                    {names.map(n => <th key={n} className="px-3 py-2.5 truncate max-w-[120px]" title={n}>{n.split(" ").slice(-1)[0]}</th>)}
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {polls.map((p) => {
                    const map = Object.fromEntries((p.results || []).map(r => [r.name, r.pct]));
                    return (
                      <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`poll-row-${p.id}`}>
                        <td className="px-5 py-2.5 text-slate-300">{p.date}</td>
                        <td className="px-3 py-2.5 text-white">{p.pollster} {p.source === "csv" && <span className="text-[9px] text-slate-500 uppercase ml-1">csv</span>}</td>
                        <td className="px-3 py-2.5 text-slate-400">{p.sample_size || "—"}</td>
                        <td className="px-3 py-2.5 text-slate-400">{p.moe ? `±${p.moe}` : "—"}</td>
                        {names.map(n => <td key={n} className="px-3 py-2.5 font-medium">{map[n] != null ? `${map[n]}%` : "—"}</td>)}
                        <td className="px-3 py-2.5 text-right">
                          <button onClick={() => removePoll(p.id)} className="text-slate-600 hover:text-rose-300" data-testid={`delete-poll-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
};

// ---------- Issues ----------
const ISSUE_SUGGESTIONS = ["Economy & Jobs", "Healthcare", "Education", "Public Safety", "Immigration",
  "Energy & Environment", "Housing", "Taxes", "Infrastructure", "Reproductive Rights"];

export const IssuesTab = ({ id, data, reload }) => {
  const [form, setForm] = useState({ issue: "", date: new Date().toISOString().slice(0, 10), salience: "", net_sentiment: "", note: "" });
  const [saving, setSaving] = useState(false);

  const addEntry = async () => {
    if (!form.issue.trim() || form.salience === "" || form.net_sentiment === "") {
      toast.error("Issue, salience and net sentiment are required"); return;
    }
    setSaving(true);
    try {
      await api.post(`/political-intelligence/candidates/${id}/issues`, {
        issue: form.issue, date: form.date,
        salience: parseFloat(form.salience), net_sentiment: parseFloat(form.net_sentiment),
        note: form.note || null,
      });
      toast.success("Issue data point added");
      setForm({ ...form, salience: "", net_sentiment: "", note: "" });
      reload();
    } catch (e) { toast.error(errMsg(e, "Could not add issue entry")); }
    finally { setSaving(false); }
  };

  const removeEntry = async (iid) => {
    try {
      await api.delete(`/political-intelligence/candidates/${id}/issues/${iid}`);
      toast.success("Entry deleted");
      reload();
    } catch (e) { toast.error(errMsg(e, "Could not delete entry")); }
  };

  return (
    <div className="space-y-4" data-testid="issues-tab">
      <Card data-testid="add-issue-card">
        <h3 className="font-display text-lg font-bold mb-1">Log public issue intelligence</h3>
        <p className="text-xs text-slate-400 mb-4">Track how much an issue matters to your electorate (salience 0–100) and how the public leans on it relative to your candidate (net sentiment −100 to +100).</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="col-span-2 md:col-span-1">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Issue</label>
            <input list="issue-suggestions" value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })}
              placeholder="Economy & Jobs" data-testid="issue-name-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
            <datalist id="issue-suggestions">{ISSUE_SUGGESTIONS.map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="issue-date-input" />
          <Input label="Salience (0–100)" type="number" min="0" max="100" value={form.salience}
            onChange={(e) => setForm({ ...form, salience: e.target.value })} placeholder="72" data-testid="issue-salience-input" />
          <Input label="Net sentiment (−100…100)" type="number" min="-100" max="100" value={form.net_sentiment}
            onChange={(e) => setForm({ ...form, net_sentiment: e.target.value })} placeholder="-12" data-testid="issue-sentiment-input" />
          <Input label="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Source / context" data-testid="issue-note-input" />
        </div>
        <div className="mt-4">
          <Btn onClick={addEntry} disabled={saving} data-testid="add-issue-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add data point
          </Btn>
        </div>
      </Card>

      {data.issues.length === 0 ? (
        <Card><EmptyState icon={Newspaper} title="No issue intelligence yet"
          description="Log the issues shaping your race — salience and sentiment over time feed directly into AI strategy scenarios." /></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.issues.map((i) => (
            <Card key={i.issue} data-testid={`issue-card-${i.issue}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-semibold">{i.issue}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{i.entries} data point{i.entries === 1 ? "" : "s"} · latest {i.last_date}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${i.latest_net_sentiment >= 0 ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-rose-300 border-rose-400/30 bg-rose-400/10"}`}>
                  Net {i.latest_net_sentiment > 0 ? "+" : ""}{i.latest_net_sentiment}
                </span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Salience</span><span>{i.latest_salience}/100</span></div>
                <div className="h-2 rounded-full bg-white/5"><div className="h-full rounded-full bg-cyan-400/70" style={{ width: `${i.latest_salience}%` }} /></div>
              </div>
              {i.sentiment_delta !== 0 && (
                <div className={`mt-2 text-xs ${i.sentiment_delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  Sentiment {i.sentiment_delta > 0 ? "+" : ""}{i.sentiment_delta} since first entry
                </div>
              )}
              <div className="mt-3 space-y-1">
                {i.series.slice(-4).reverse().map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="w-20 shrink-0">{e.date}</span>
                    <span>Sal {e.salience}</span>
                    <span className={e.net_sentiment >= 0 ? "text-emerald-300/80" : "text-rose-300/80"}>Net {e.net_sentiment > 0 ? "+" : ""}{e.net_sentiment}</span>
                    {e.note && <span className="truncate text-slate-600 flex-1">· {e.note}</span>}
                    <button onClick={() => removeEntry(e.id)} className="ml-auto text-slate-600 hover:text-rose-300" data-testid={`delete-issue-${e.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- AI Strategy ----------
const POSTURE_STYLES = {
  persuasion: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
  mobilization: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  efficiency: "text-amber-300 border-amber-400/30 bg-amber-400/10",
};

export const StrategyTab = ({ id, data, reload }) => {
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(null);
  const [budget, setBudget] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState(null);
  const [journeyBusy, setJourneyBusy] = useState(false);

  useEffect(() => {
    api.get(`/political-intelligence/candidates/${id}/strategy`).then(r => setStrategy(r.data));
  }, [id]);

  const applyScenario = async (idx) => {
    setApplying(idx);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/strategy/apply`, { scenario_index: idx });
      setApplied({ ...r.data, scenario_index: idx });
      toast.success(`Draft campaign + media plan created — auto-linked to Before vs After`);
      if (reload) reload();
    } catch (e) { toast.error(errMsg(e, "Could not build campaign from scenario")); }
    finally { setApplying(null); }
  };

  const draftJourney = async () => {
    setJourneyBusy(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/strategy/journey`, {
        scenario_index: applied.scenario_index, campaign_id: applied.campaign_id,
      });
      setApplied(a => ({ ...a, journey: r.data }));
      toast.success("Matching journey drafted — attach your lists, then launch");
    } catch (e) { toast.error(errMsg(e, "Could not draft journey")); }
    finally { setJourneyBusy(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/strategy`, {
        total_budget: budget ? parseFloat(budget) : null, notes: notes || null,
      });
      setStrategy(r.data);
      setApplied(null);
      toast.success("Strategy scenarios generated");
    } catch (e) { toast.error(errMsg(e, "Strategy generation failed")); }
    finally { setGenerating(false); }
  };

  const scenarios = strategy?.scenarios || [];

  return (
    <div className="space-y-4" data-testid="strategy-tab">
      <Card data-testid="strategy-generate-card">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold">AI Strategy Scenarios</h3>
            <p className="text-xs text-slate-400 mt-1">Claude analyzes your polling averages, momentum and issue intelligence to draft 3 distinct budget / media-mix / geography scenarios — planning estimates only.</p>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <Input label="Budget hint ($, optional)" type="number" value={budget} onChange={(e) => setBudget(e.target.value)}
              placeholder="75000" className="w-40" data-testid="strategy-budget-input" />
            <Input label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Focus on GOTV…" className="w-48" data-testid="strategy-notes-input" />
            <Btn onClick={generate} disabled={generating} data-testid="generate-strategy-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {scenarios.length ? "Regenerate" : "Generate scenarios"}
            </Btn>
          </div>
        </div>
        {data.polling.poll_count === 0 && (
          <div className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-400/20 rounded-md p-2.5">
            Tip: add polls and issues first — scenarios grounded in real race data are far sharper.
          </div>
        )}
      </Card>

      {applied && (
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] p-4 flex items-start gap-3 flex-wrap" data-testid="scenario-applied-banner">
          <Rocket className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold text-emerald-200">Draft campaign & media plan created</div>
            <p className="text-xs text-emerald-200/80 mt-0.5">"{applied.campaign_name}" is ready to review — it's auto-linked to this race's Before vs After panel.{applied.excluded_channels?.length ? ` Excluded (run via Journeys): ${applied.excluded_channels.join(", ")}.` : ""}</p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            {!applied.journey ? (
              <Btn variant="secondary" onClick={draftJourney} disabled={journeyBusy} data-testid="draft-journey-btn">
                {journeyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Draft matching journey
              </Btn>
            ) : (
              <Btn variant="secondary" onClick={() => navigate("/journeys")} data-testid="open-journeys-btn">
                Open Journeys <ArrowUpRight className="w-3.5 h-3.5" />
              </Btn>
            )}
            <Btn variant="secondary" onClick={() => navigate(`/campaigns/${applied.campaign_id}`)} data-testid="open-created-campaign-btn">
              Open campaign <ArrowUpRight className="w-3.5 h-3.5" />
            </Btn>
            <Btn variant="secondary" onClick={() => navigate("/media-plans")} data-testid="open-media-plans-btn">
              Media plan <ArrowUpRight className="w-3.5 h-3.5" />
            </Btn>
          </div>
        </div>
      )}

      {strategy?.causality_note && scenarios.length > 0 && (
        <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-4 flex items-start gap-3" data-testid="causality-note">
          <Scale className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200/90 leading-relaxed">{strategy.causality_note}</div>
        </div>
      )}

      {generating && scenarios.length === 0 && (
        <Card><div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Analyzing your race and drafting scenarios…</div></Card>
      )}

      {scenarios.length === 0 && !generating ? (
        <Card><EmptyState icon={Sparkles} title="No scenarios yet"
          description="Generate AI strategy scenarios — each takes a different posture (persuasion, mobilization, efficiency) grounded in your race intelligence." /></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          {scenarios.map((s, idx) => (
            <Card key={idx} data-testid={`scenario-card-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-bold">{s.name}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${POSTURE_STYLES[s.posture] || POSTURE_STYLES.persuasion}`}>{s.posture}</span>
              </div>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">{s.summary}</p>
              <div className="mt-3 flex gap-4 text-sm">
                {s.total_budget != null && <div><span className="text-slate-500 text-xs">Budget</span><div className="font-semibold">${Number(s.total_budget).toLocaleString()}</div></div>}
                {s.duration_weeks != null && <div><span className="text-slate-500 text-xs">Flight</span><div className="font-semibold">{s.duration_weeks} wks</div></div>}
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Tv className="w-3 h-3" /> Media mix</div>
                <div className="space-y-1.5">
                  {(s.media_mix || []).map((m) => (
                    <div key={m.channel} title={m.rationale}>
                      <div className="flex justify-between text-xs"><span className="text-slate-300">{m.channel}</span><span className="text-cyan-300 font-medium">{m.pct}%</span></div>
                      <div className="h-1.5 rounded-full bg-white/5 mt-0.5"><div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${m.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
              {(s.geography || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Target className="w-3 h-3" /> Geography</div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.geography.map((g) => (
                      <span key={g.area} title={g.rationale}
                        className={`text-[10px] px-2 py-1 rounded-full border ${g.priority === "high" ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10" : g.priority === "medium" ? "border-white/15 text-slate-300 bg-white/5" : "border-white/10 text-slate-500"}`}>
                        {g.area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(s.key_messages || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Key messages</div>
                  <ul className="space-y-1">
                    {s.key_messages.map((m, i) => <li key={i} className="text-xs text-slate-300 leading-relaxed">· {m}</li>)}
                  </ul>
                </div>
              )}
              {(s.delivery_goals || []).length > 0 && (
                <div className="mt-4 rounded-md bg-white/[0.03] border border-white/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Delivery goals (planning estimates)</div>
                  {s.delivery_goals.map((g, i) => <div key={i} className="text-xs text-slate-400">· {g}</div>)}
                </div>
              )}
              <Btn className="mt-4 w-full justify-center" onClick={() => applyScenario(idx)}
                disabled={applying !== null} data-testid={`apply-scenario-${idx}`}>
                {applying === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Use this scenario
              </Btn>
            </Card>
          ))}
        </div>
      )}
      {strategy?.generated_at && scenarios.length > 0 && (
        <div className="text-xs text-slate-600">Generated {new Date(strategy.generated_at).toLocaleString()} · TotalReach planning estimates — not a prediction of polling or election outcomes.</div>
      )}
    </div>
  );
};

// ---------- Before vs After ----------
export const BeforeAfterTab = ({ id, data, reload }) => {
  const [ba, setBa] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [linking, setLinking] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const loadBa = useCallback(() => {
    api.get(`/political-intelligence/candidates/${id}/before-after`).then(r => setBa(r.data));
  }, [id]);
  useEffect(() => {
    loadBa();
    api.get("/campaigns").then(r => setCampaigns(r.data || [])).catch(() => {});
  }, [loadBa]);

  const capture = async () => {
    setCapturing(true);
    try {
      await api.post(`/political-intelligence/candidates/${id}/baseline`, {});
      toast.success("Baseline snapshot captured");
      loadBa(); reload();
    } catch (e) { toast.error(errMsg(e, "Could not capture baseline")); }
    finally { setCapturing(false); }
  };

  const toggleCampaign = async (cid) => {
    const current = data.candidate.linked_campaign_ids || [];
    const next = current.includes(cid) ? current.filter(x => x !== cid) : [...current, cid];
    setLinking(true);
    try {
      await api.put(`/political-intelligence/candidates/${id}`, { linked_campaign_ids: next });
      reload(); loadBa();
    } catch (e) { toast.error(errMsg(e, "Could not update linked campaigns")); }
    finally { setLinking(false); }
  };

  if (!ba) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2 text-cyan-300" />Loading measurement…</div>;

  const linked = data.candidate.linked_campaign_ids || [];

  return (
    <div className="space-y-4" data-testid="beforeafter-tab">
      <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-4 flex items-start gap-3" data-testid="measurement-note">
        <Scale className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-amber-200">Honest measurement</div>
          <p className="text-xs text-amber-200/80 mt-0.5 leading-relaxed">{ba.measurement_note}</p>
        </div>
      </div>

      {!ba.baseline ? (
        <Card>
          <EmptyState icon={Camera} title="No baseline captured yet"
            description="Capture a 'Before' snapshot of your current polling averages and issue landscape. Later, compare it against fresh public data — shown separately from what your campaign delivered."
            action={<Btn onClick={capture} disabled={capturing} data-testid="capture-baseline-btn">
              {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Capture baseline snapshot
            </Btn>} />
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-slate-400">
              Baseline <span className="text-white font-medium">{ba.baseline.label}</span> · captured {ba.baseline.captured_at?.slice(0, 10)}
            </div>
            <Btn variant="secondary" onClick={capture} disabled={capturing} data-testid="recapture-baseline-btn">
              {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Capture new baseline
            </Btn>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-cyan-400/20" data-testid="public-indicators-panel">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-cyan-300" />
                <h3 className="font-display text-lg font-bold">Public indicators</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Polling & issue sentiment — public opinion data, independent of your advertising.</p>
              {ba.public_deltas.length === 0 ? (
                <div className="text-sm text-slate-500">No comparable polling yet — add polls dated after the baseline to see movement.</div>
              ) : (
                <div className="space-y-3">
                  {ba.public_deltas.map((d) => (
                    <div key={d.name} className="flex items-center gap-3" data-testid={`delta-row-${d.name}`}>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${d.is_mine ? "text-cyan-300 font-medium" : "text-white"}`}>{d.name}</div>
                      </div>
                      <div className="text-xs text-slate-500">{d.before}%</div>
                      <div className="text-slate-600">→</div>
                      <div className="text-sm font-semibold">{d.after}%</div>
                      <span className={`text-xs w-14 text-right ${d.delta > 0 ? "text-emerald-300" : d.delta < 0 ? "text-rose-300" : "text-slate-500"}`}>
                        {d.delta > 0 ? "+" : ""}{d.delta}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(ba.current.issues || []).length > 0 && (
                <div className="mt-5 pt-4 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Issue landscape (latest)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {ba.current.issues.slice(0, 8).map((i) => (
                      <span key={i.issue} className={`text-[10px] px-2 py-1 rounded-full border ${i.latest_net_sentiment >= 0 ? "border-emerald-400/25 text-emerald-300/90" : "border-rose-400/25 text-rose-300/90"}`}>
                        {i.issue} {i.latest_net_sentiment > 0 ? "+" : ""}{i.latest_net_sentiment}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="border-blue-400/20" data-testid="campaign-delivery-panel">
              <div className="flex items-center gap-2 mb-1">
                <Tv className="w-4 h-4 text-blue-300" />
                <h3 className="font-display text-lg font-bold">Campaign delivery</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">What your campaigns actually delivered since the baseline — reach, impressions and spend.</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 text-center">
                  <div className="font-display text-xl font-bold">${Number(ba.campaign_delivery.totals.spend || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Spend</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 text-center">
                  <div className="font-display text-xl font-bold">{Number(ba.campaign_delivery.totals.impressions || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Impressions</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 text-center">
                  <div className="font-display text-xl font-bold">{Number(ba.campaign_delivery.totals.households || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Households</div>
                </div>
              </div>
              {ba.campaign_delivery.campaigns.length === 0 ? (
                <div className="text-sm text-slate-500">No campaigns linked — attach the campaigns you're running for this race below.</div>
              ) : (
                <div className="space-y-2">
                  {ba.campaign_delivery.campaigns.map((c) => (
                    <div key={c.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between gap-3" data-testid={`delivery-campaign-${c.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.status} · ${Number(c.budget).toLocaleString()} budget{c.channels?.length ? ` · ${c.channels.slice(0, 2).join(", ")}` : ""}</div>
                      </div>
                      <div className="text-right text-xs shrink-0">
                        {c.spend != null ? (
                          <>
                            <div className="text-white font-medium">${Number(c.spend).toLocaleString()}</div>
                            <div className="text-slate-500">{Number(c.impressions || 0).toLocaleString()} imp</div>
                          </>
                        ) : <span className="text-slate-600">{c.delivery_error || "Not delivering yet"}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      <Card data-testid="link-campaigns-card">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Linked campaigns</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Select the advertising campaigns running for this race — their delivery appears in the Campaign Delivery panel.</p>
        {campaigns.length === 0 ? <div className="text-sm text-slate-500">No campaigns in your account yet.</div> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto">
            {campaigns.map((c) => (
              <label key={c.id} className={`flex items-center gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors ${linked.includes(c.id) ? "border-cyan-400/40 bg-cyan-400/[0.06]" : "border-white/10 hover:border-white/20"}`}
                data-testid={`link-campaign-${c.id}`}>
                <input type="checkbox" checked={linked.includes(c.id)} disabled={linking} onChange={() => toggleCampaign(c.id)}
                  className="accent-cyan-400" />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500">{c.status} · ${Number(c.total_budget || 0).toLocaleString()}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
